import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Send, Trash2, Plus, Power, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { iotApi } from '../lib/iotApi';

interface Sector { id: string; name: string; }
interface Schedule {
  id: string;
  sector_id: string;
  recipients: string[];
  frequency: 'weekly' | 'monthly';
  day_of_week: number;
  day_of_month: number;
  hour: number;
  minute: number;
  active: boolean;
  format: 'pdf' | 'csv';
  last_sent_at?: string | null;
}

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const emptyForm = {
  sector_id: '',
  recipients: '',
  frequency: 'weekly' as 'weekly' | 'monthly',
  day_of_week: 1,
  day_of_month: 1,
  hour: 7,
  minute: 0,
  format: 'pdf' as 'pdf' | 'csv',
  active: true,
};

const ReportSchedules: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const refresh = useCallback(async () => {
    try {
      const [sc, se] = await Promise.all([
        iotApi.get('/iot/report-schedules'),
        iotApi.get('/iot/sectors'),
      ]);
      setSchedules(sc);
      setSectors(se);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (sectors.length) {
      setForm((f) => (f.sector_id ? f : { ...f, sector_id: sectors[0].id }));
    }
  }, [sectors]);

  const sectorName = (id: string) => sectors.find((s) => s.id === id)?.name || id;

  const submit = async () => {
    const recipients = form.recipients.split(/[,;\s]+/).map((r) => r.trim()).filter(Boolean);
    if (!form.sector_id) return toast.error('Sélectionnez un secteur.');
    if (recipients.length === 0) return toast.error('Ajoutez au moins un destinataire.');
    try {
      await iotApi.post('/iot/report-schedules', {
        sector_id: form.sector_id,
        recipients,
        frequency: form.frequency,
        day_of_week: form.day_of_week,
        day_of_month: form.day_of_month,
        hour: form.hour,
        minute: form.minute,
        format: form.format,
        active: form.active,
      });
      toast.success('Rapport planifié créé.');
      setShowForm(false);
      setForm({ ...emptyForm, sector_id: sectors[0]?.id || '' });
      refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const toggleActive = async (s: Schedule) => {
    try {
      await iotApi.put(`/iot/report-schedules/${s.id}`, { active: !s.active });
    } catch (e) {
      toast.error((e as Error).message);
    }
    refresh();
  };

  const remove = async (s: Schedule) => {
    try {
      await iotApi.del(`/iot/report-schedules/${s.id}`);
      toast.success('Planification supprimée.');
    } catch (e) {
      toast.error((e as Error).message);
    }
    refresh();
  };

  const sendNow = async (s: Schedule) => {
    try {
      await iotApi.post(`/iot/report-schedules/${s.id}/send-now`);
      toast.success('Bilan envoyé (voir logs en preview).');
      refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-heading-4 text-[var(--text-primary)] flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-[var(--color-primary)]" />
          Rapports automatiques par secteur
        </h2>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-3 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 text-sm"
          data-testid="report-schedule-new"
        >
          <Plus className="w-4 h-4" /> Planifier
        </button>
      </div>

      {showForm && (
        <div className="card mb-4" data-testid="report-schedule-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Secteur</label>
              <select
                value={form.sector_id}
                onChange={(e) => setForm({ ...form, sector_id: e.target.value })}
                className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="rs-sector"
              >
                {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Destinataires (séparés par des virgules)</label>
              <input
                type="text"
                value={form.recipients}
                onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                placeholder="client@exemple.fr, partenaire@exemple.fr"
                className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="rs-recipients"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Fréquence</label>
              <select
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value as 'weekly' | 'monthly' })}
                className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="rs-frequency"
              >
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuel</option>
              </select>
            </div>
            {form.frequency === 'weekly' ? (
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Jour d'envoi</label>
                <select
                  value={form.day_of_week}
                  onChange={(e) => setForm({ ...form, day_of_week: parseInt(e.target.value, 10) })}
                  className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                  data-testid="rs-day-week"
                >
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Jour du mois (1-28)</label>
                <input
                  type="number" min={1} max={28} value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value || '1', 10) })}
                  className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                  data-testid="rs-day-month"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-1">Heure d'envoi</label>
              <input
                type="time"
                value={`${String(form.hour).padStart(2, '0')}:${String(form.minute).padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map((x) => parseInt(x, 10));
                  setForm({ ...form, hour: h || 0, minute: m || 0 });
                }}
                className="w-full border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="rs-time"
              />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input id="rs-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" data-testid="rs-active" />
              <label htmlFor="rs-active" className="text-sm text-[var(--text-primary)]">Actif</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-[var(--border-medium)] rounded-lg text-[var(--text-secondary)]" data-testid="rs-cancel">Annuler</button>
            <button onClick={submit} className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg" data-testid="rs-submit">Créer</button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="report-schedule-table">
          <thead>
            <tr className="border-b border-[var(--border-light)] text-left text-[var(--text-secondary)]">
              <th className="px-4 py-3 font-medium">Secteur</th>
              <th className="px-4 py-3 font-medium">Fréquence</th>
              <th className="px-4 py-3 font-medium">Envoi</th>
              <th className="px-4 py-3 font-medium">Destinataires</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id} className="border-b border-[var(--border-light)] last:border-0" data-testid={`report-schedule-${s.id}`}>
                <td className="px-4 py-3 text-[var(--text-primary)]">{sectorName(s.sector_id)}</td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{s.frequency === 'weekly' ? 'Hebdomadaire' : 'Mensuel'}</td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">
                  {s.frequency === 'weekly' ? DAYS[s.day_of_week] : `Jour ${s.day_of_month}`} à {String(s.hour).padStart(2, '0')}:{String(s.minute).padStart(2, '0')}
                </td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">
                  <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{s.recipients.length}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${s.active ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]' : 'bg-[var(--gray-100)] text-[var(--text-secondary)]'}`}>
                    {s.active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => sendNow(s)} className="p-1.5 rounded-md hover:bg-[var(--gray-50)]" title="Envoyer maintenant" data-testid={`rs-send-${s.id}`}>
                      <Send className="w-4 h-4 text-[var(--color-primary)]" />
                    </button>
                    <button onClick={() => toggleActive(s)} className="p-1.5 rounded-md hover:bg-[var(--gray-50)]" title={s.active ? 'Désactiver' : 'Activer'} data-testid={`rs-toggle-${s.id}`}>
                      <Power className={`w-4 h-4 ${s.active ? 'text-[var(--color-success)]' : 'text-[var(--text-secondary)]'}`} />
                    </button>
                    <button onClick={() => remove(s)} className="p-1.5 rounded-md hover:bg-red-50 text-[var(--color-danger)]" title="Supprimer" data-testid={`rs-delete-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {schedules.length === 0 && (
          <div className="text-center py-8 text-[var(--text-secondary)]" data-testid="report-schedule-empty">
            Aucun rapport planifié. Cliquez sur « Planifier » pour en créer un.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportSchedules;
